import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useChat } from "../../contexts/ChatContext";
import { useNotifications } from "../../contexts/NotificationContext";
import NotificationDropdown from "../Notifications/NotificationDropdown";
import StashitLogo from "../Logo/StashitLogo";

interface LayoutDashProps {
  children: React.ReactNode;
}

const LayoutDash: React.FC<LayoutDashProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/auth/login");
    setIsMenuOpen(false);
  };

  const { chatRooms } = useChat();
  const { unreadCount: systemUnreadCount } = useNotifications();
  const unreadCount = chatRooms.reduce((total, room) => total + room.unread_count, 0);

  // Determine if user is admin (used in mobile nav)
  const isAdmin = user?.role === 'admin';

  // Errand-focused navigation links
  const getNavLinks = () => {
    if (!user) return [];

    // Determine user roles properly
    const isDriver = user.isDriver || user.role === 'driver' || user.roles?.includes('driver');
    const isCourier = user.isCourier || user.role === 'courier' || user.roles?.includes('courier');
    const isAdmin = user.isAdmin || user.role === 'admin' || user.roles?.includes('admin');

    const links = [
      { to: "/", label: "Home", icon: "🏠" },
      {
        to: isCourier ? "/courier/errands" : isDriver ? "/driver/errands" : "/errand/request",
        label: isDriver || isCourier ? "My Jobs" : "Request Errand",
        icon: "📝"
      },
      { to: "/chat", label: "Chat", icon: "💬", badge: unreadCount },
      { to: "/analytics", label: "Analytics", icon: "📊" },
      { to: "/payments", label: "Payments", icon: "💰" },
      { to: "/support", label: "Support", icon: "🆘" },
      { to: "/profile", label: "Profile", icon: "👤" },
    ];

    // Add "Become" options for users who don't have these roles
    if (!isDriver) {
      links.splice(2, 0, { to: "/driver/apply", label: "Become a Driver", icon: "🚗" });
    }
    if (!isCourier) {
      links.splice(3, 0, { to: "/courier/register", label: "Become a Courier", icon: "👥" });
    }

    // Admin links - show prominently for admin users
    if (isAdmin) {
      const adminLinks = [
        { to: "/admin", label: "Admin Dashboard", icon: "⚙️" },
        { to: "/admin/users", label: "Manage Users", icon: "👥" },
        { to: "/admin/couriers", label: "Couriers", icon: "🚚" },
        { to: "/admin/driver-applications", label: "Driver Apps", icon: "🚗" },
        { to: "/admin/analytics", label: "Admin Analytics", icon: "📈" },
        { to: "/admin/payments", label: "Payments", icon: "💳" },
        { to: "/admin/escrow", label: "Escrow", icon: "💰" }
      ];
      // Add admin links at the beginning
      links.unshift(...adminLinks);
    }

    // Courier-specific links
    if (isCourier) {
      links.push(
        { to: "/courier/dashboard", label: "Courier Dashboard", icon: "🚚" },
        { to: "/courier/errands", label: "My Jobs", icon: "💼" },
        { to: "/errands", label: "My Errands", icon: "📦" },
        { to: "/courier/earnings", label: "Earnings", icon: "💰", badge: systemUnreadCount }
      );
    }
    // Driver-specific links
    else if (isDriver) {
      links.push(
        { to: "/driver/dashboard", label: "Driver Dashboard", icon: "🚗" },
        { to: "/driver/errands", label: "My Jobs", icon: "✅" },
        { to: "/errands", label: "My Errands", icon: "📦" }
      );
    }
    else {
      // Regular users see "My Errands"
      links.push(
        { to: "/errands", label: "My Errands", icon: "📦" }
      );
    }

    return links;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Sticky for mobile */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-green-600 via-green-700 to-green-800 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">N</span>
              </div>
              <span className="text-xl font-bold">NkeleGo</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-2 overflow-x-auto max-w-full">
              {/* Always show Admin link first if user is admin */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="relative px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap flex-shrink-0"
                >
                  <span className="mr-1">⚙️</span>
                  Admin
                </Link>
              )}
              {getNavLinks()
                .filter(link => !isAdmin || link.to !== '/admin') // Don't duplicate admin link
                .slice(0, 8) // Limit to prevent overflow
                .map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="relative px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap flex-shrink-0"
                  >
                    <span className="mr-1">{link.icon}</span>
                    {link.label}
                    {link.badge && link.badge > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {link.badge > 99 ? '99+' : link.badge}
                      </span>
                    )}
                  </Link>
                ))}

              {/* Notifications */}
              <div className="relative ml-2">
                <button
                  onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                  className="relative p-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  🔔
                  {systemUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {systemUnreadCount > 99 ? '99+' : systemUnreadCount}
                    </span>
                  )}
                </button>
                <NotificationDropdown
                  isOpen={isNotificationOpen}
                  onClose={() => setIsNotificationOpen(false)}
                />
              </div>

              {/* User Menu */}
              {user && (
                <div className="ml-4 flex items-center space-x-2">
                  <span className="text-sm">{user.name || user.email}</span>
                  <button
                    onClick={handleLogout}
                    className="px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    Logout
                  </button>
                </div>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-2">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                  className="relative p-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  🔔
                  {systemUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {systemUnreadCount > 99 ? '99+' : systemUnreadCount}
                    </span>
                  )}
                </button>
                <NotificationDropdown
                  isOpen={isNotificationOpen}
                  onClose={() => setIsNotificationOpen(false)}
                />
              </div>

              {/* Hamburger Menu */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-lg hover:bg-green-700 transition-colors"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-green-800 border-t border-green-700">
            <div className="px-4 py-3 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
              {getNavLinks().map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className="relative flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-green-700 transition-colors text-white"
                >
                  <span className="text-xl">{link.icon}</span>
                  <span className="flex-1 font-medium">{link.label}</span>
                  {link.badge && link.badge > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {link.badge > 99 ? '99+' : link.badge}
                    </span>
                  )}
                </Link>
              ))}

              {/* User Info & Logout */}
              {user && (
                <>
                  <div className="border-t border-green-700 my-2"></div>
                  <div className="px-4 py-2 text-sm text-green-100">
                    <p className="font-medium">{user.name || user.email}</p>
                    <p className="text-xs capitalize">{user.role || 'user'}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-green-700 transition-colors text-white font-medium"
                  >
                    <span>🚪</span>
                    <span>Logout</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 min-h-[calc(100vh-80px)] pb-20 md:pb-0">
        {children}
      </main>

      {/* Mobile Bottom Navigation Bar */}
      {user && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="flex items-center justify-around h-16 px-0.5 sm:px-1">
            {(() => {
              // For mobile, prioritize essential links
              const allLinks = getNavLinks();

              // For admin users, prioritize admin-specific links
              let mobileLinks;
              if (isAdmin) {
                mobileLinks = [
                  allLinks.find(l => l.to === '/admin'), // Admin Dashboard
                  allLinks.find(l => l.to === '/admin/driver-applications'), // Manage Drivers
                  allLinks.find(l => l.to === '/admin/users'), // Users
                  allLinks.find(l => l.to === '/admin/analytics'), // Analytics
                  allLinks.find(l => l.to === '/profile') // Profile
                ].filter(Boolean); // Remove any undefined entries

                // If we don't have enough admin links, fill with general links
                if (mobileLinks.length < 5) {
                  const remainingSlots = 5 - mobileLinks.length;
                  const generalLinks = allLinks.filter(l =>
                    !l.to.startsWith('/admin') &&
                    !mobileLinks.some(ml => ml.to === l.to)
                  ).slice(0, remainingSlots);
                  mobileLinks = [...mobileLinks, ...generalLinks];
                }
              } else {
                mobileLinks = allLinks.slice(0, 5);
              }

              return mobileLinks.map((link) => {
                // Shorten long labels for mobile
                const displayLabel = link.label.length > 10
                  ? link.label === 'Payments' ? 'Pay'
                  : link.label === 'Analytics' ? 'Stats'
                  : link.label === 'Request Errand' ? 'Request'
                  : link.label === 'Admin Dashboard' ? 'Admin'
                  : link.label === 'Manage Drivers' ? 'Drivers'
                  : link.label.substring(0, 8) + '...'
                  : link.label;

              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex flex-col items-center justify-center flex-1 h-full text-gray-600 hover:text-green-600 transition-colors relative min-w-0 px-0.5 sm:px-1"
                  title={link.label}
                >
                  <span className="text-lg sm:text-xl mb-0.5">{link.icon}</span>
                  <span className="text-[9px] sm:text-[10px] font-medium leading-tight text-center truncate w-full px-0.5">
                    {displayLabel}
                  </span>
                  {link.badge && link.badge > 0 && (
                    <span className="absolute top-0.5 right-1/2 translate-x-3 bg-red-500 text-white text-[10px] font-bold rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 flex items-center justify-center">
                      {link.badge > 9 ? '9+' : link.badge}
                    </span>
                  )}
                </Link>
              );
              });
            })()}
          </div>
        </nav>
      )}
    </div>
  );
};

export default LayoutDash;
